import Modal, { ModalContent, ModalCTAs } from "@/components/molecules/Modal";
import { useTranslation } from "react-i18next";
import React from "react";
import CTA from "@/components/atoms/CTA";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContractBulkEditStore } from "@/stores/contractBulkEditStore";
import ContractLightTable from "@/components/organisms/ContractLightTable";
import { BULK_EDIT_VISIBLE_COLUMNS } from "@/components/organisms/ContractLightTable/const/default";
import Text from "@/components/atoms/Text";
import { Group } from "@mantine/core";
import Select from "@/components/atoms/Select";
import { useAuth } from "@/contexts/AuthContext";
import NumberInput from "@/components/atoms/NumberInput";
import Divider from "@/components/atoms/Divider";
import { PriceIncreaseType } from "@/types";
import { PriceIncreaseTypeEnum } from "@/constants/enums/PriceIncreaseTypeEnum";
import { postBulkUpdateContractPrices } from "@/services/Contracts";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

export default function BulkContractUpdate() {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [selectedIncreaseType, setSelectedIncreaseType] = React.useState<PriceIncreaseType>(
    PriceIncreaseTypeEnum.PERCENTAGE
  );
  const [priceIncrease, setPriceIncrease] = React.useState<number>(0);
  const isModalOpen = useContractBulkEditStore((s) => s.isModalOpen);
  const closeModal = useContractBulkEditStore((s) => s.closeModal);
  const selectedRows = useContractBulkEditStore((s) => s.selectedRows);
  const clearSelection = useContractBulkEditStore((s) => s.clearSelection);

  const queryClient = useQueryClient();

  const { mutate: updateContractPrices, isPending } = useMutation({
    mutationFn: async () => {
      const contractIds = selectedRows.map((row) => row.id);
      return await postBulkUpdateContractPrices(contractIds, priceIncrease, selectedIncreaseType);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getContractsLight"] });
      clearSelection();
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.updateSuccess"),
          t("notifications:contracts.bulkUpdateContracts")
        )
      );
      closeModal();
    },
    onError: (err) => {
      console.error(err);
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.updateError"),
          t("notifications:contracts.bulkUpdateContractsError")
        )
      );
    },
  });

  return (
    <Modal
      opened={isModalOpen}
      onClose={closeModal}
      title={t("contracts:bulkUpdateContracts")}
      showCloseButton={true}
      size={"xl"}
    >
      <ModalContent>
        <Text mt={12} mb={2} ml={0} color={"primary"} size={"sm"} variant={"text"}>
          {t("contracts:updateByPercentageOrFixedSum")}
        </Text>
        <Group>
          <NumberInput
            value={priceIncrease}
            onChange={(val) => setPriceIncrease(val as number)}
            suffix={
              selectedIncreaseType === PriceIncreaseTypeEnum.PERCENTAGE ? "%" : ` ${currentCompany?.companyCurrency}`
            }
            max={selectedIncreaseType === PriceIncreaseTypeEnum.PERCENTAGE ? 100 : undefined}
            min={selectedIncreaseType === PriceIncreaseTypeEnum.PERCENTAGE ? 0 : undefined}
          />
          <Select
            value={selectedIncreaseType}
            onChange={(val) => setSelectedIncreaseType(val as PriceIncreaseType)}
            data={[
              {
                label: t("contracts:percentageIncrease"),
                value: PriceIncreaseTypeEnum.PERCENTAGE,
              },
              {
                label: t("contracts:fixedIncrease"),
                value: PriceIncreaseTypeEnum.FIXED,
              },
            ]}
          />
        </Group>
        <Divider />
        <ContractLightTable actionable={false} visibleColumns={BULK_EDIT_VISIBLE_COLUMNS} localRows={selectedRows} />
      </ModalContent>
      <ModalCTAs>
        <CTA id="back-create-sales-order" intent="negate" onClick={closeModal} disabled={isPending} loading={isPending}>
          {t("actions:cancel")}
        </CTA>
        <CTA
          id="next-create-sales-order"
          intent="primary"
          loading={isPending}
          disabled={priceIncrease === 0 || isPending}
          onClick={() => updateContractPrices()}
        >
          {t("actions:update")}
        </CTA>
      </ModalCTAs>
    </Modal>
  );
}
